// Executive logs — the authenticator code asked before every export (Leon,
// 2026-10-29: viewing needs no code, exporting does). The server is the lock
// (withRecentTotp on /exec-logs/:log/export); this popup only collects the code.

import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@bednbody/ui/components/input-otp";
import { Label } from "@bednbody/ui/components/label";
import { LockSimpleIcon } from "@phosphor-icons/react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useEffect, useId, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

export function CodeDialog({
  open,
  busy,
  onCancel,
  onVerified,
}: {
  open: boolean;
  /** The export is running after a good code. */
  busy: boolean;
  onCancel: () => void;
  onVerified: () => void;
}) {
  const { verifyMfa } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const codeId = useId();

  useEffect(() => {
    if (open) {
      setCode("");
      setError(null);
    }
  }, [open]);

  async function submit(value: string) {
    if (value.length !== 6 || checking || busy) return;
    setChecking(true);
    setError(null);
    const { data } = await supabase.auth.mfa.listFactors();
    const factor = data?.totp.find((f) => f.status === "verified");
    if (!factor) {
      setChecking(false);
      setError("No authenticator is set up on this account.");
      return;
    }
    const { error: verifyError } = await verifyMfa(factor.id, value);
    setChecking(false);
    if (verifyError) {
      setCode("");
      setError("That code didn't work. Try the current one.");
      return;
    }
    onVerified();
  }

  const working = checking || busy;

  return (
    <Dialog open={open} onOpenChange={(next) => (next || working ? null : onCancel())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockSimpleIcon className="size-5" /> Enter your code to export
          </DialogTitle>
          <DialogDescription>
            Exporting a log needs the 6-digit code from your authenticator app, every time.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submit(code);
          }}
        >
          <Label className="sr-only" htmlFor={codeId}>
            6-digit code
          </Label>
          <InputOTP
            autoFocus
            disabled={working}
            id={codeId}
            maxLength={6}
            onChange={setCode}
            onComplete={(value) => void submit(value)}
            pattern={REGEXP_ONLY_DIGITS}
            value={code}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          <DialogFooter className="w-full">
            <Button disabled={working} onClick={onCancel} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={working || code.length !== 6} type="submit">
              {checking ? "Checking…" : busy ? "Exporting…" : "Export"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
